/**
 * PreSpendGateBanner — operator-facing surface for the pre-spend gate.
 * Shown above any spend action (Order, Permit, Allocate, Assign) when
 * the gate is not yet cleared.
 *
 * Tone: factual + specific. Operators see exactly which gate is blocking
 * and what unblocks it — no vague "blocked" copy.
 */
import { Lock, ShieldCheck, ShieldAlert, Clock, FileCheck, Wallet } from "lucide-react";
import type { PreSpendGate, PreSpendGateName } from "@/data/preSpendGate";

const GATE_ICON: Record<PreSpendGateName, React.ElementType> = {
  review: FileCheck,
  rescission: Clock,
  financial: Wallet,
};

interface Props {
  gate: PreSpendGate;
  /** Optional context line, e.g. "Materials cannot be ordered yet." */
  context?: string;
  /** Compact = inline single-row variant for use inside modals. */
  compact?: boolean;
}

export function PreSpendGateBanner({ gate, context, compact = false }: Props) {
  if (gate.cleared) {
    if (compact) {
      return (
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-success">
          <ShieldCheck className="h-3 w-3" /> Cleared to spend
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-success/30 bg-success/5 px-3 py-2 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-success shrink-0" />
        <p className="text-[11px] font-semibold text-foreground">All pre-spend gates cleared — safe to order, permit, allocate.</p>
      </div>
    );
  }

  const blockers = gate.checks.filter((c) => !c.cleared);

  if (compact) {
    return (
      <div className="rounded-md border border-warning/30 bg-warning/5 px-2.5 py-1.5 flex items-center gap-2">
        <Lock className="h-3 w-3 text-warning shrink-0" />
        <p className="text-[11px] font-semibold text-warning truncate">
          {gate.shortReason}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 text-warning shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-bold text-foreground">
            {context ?? "Pre-spend gates not cleared"}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Anything that obligates company money or inventory is blocked until all three gates clear.
          </p>
        </div>
      </div>
      <div className="grid gap-1.5 pl-6">
        {gate.checks.map((c) => {
          const Icon = GATE_ICON[c.name];
          return (
            <div
              key={c.name}
              className={
                "flex items-center gap-2 rounded-md border px-2.5 py-1.5 " +
                (c.cleared
                  ? "border-success/30 bg-success/5"
                  : "border-warning/40 bg-card")
              }
            >
              <Icon className={"h-3.5 w-3.5 shrink-0 " + (c.cleared ? "text-success" : "text-warning")} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-foreground">
                  {c.label} {c.cleared ? "✓" : ""}
                </p>
                <p className="text-[10px] text-muted-foreground">{c.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
